import React, { useState, useEffect, useCallback } from 'react';
import {
  FiPlus, FiEdit2, FiTrash2, FiRefreshCw,
  FiToggleLeft, FiToggleRight, FiPackage, FiStar
} from 'react-icons/fi';
import { superAdminAPI } from '../../services/api';
import Modal from '../../components/shared/Modal';

const EMPTY_FORM = {
  name: '',
  sms_count: '',
  price: '',
  badge: '',
  is_popular: false,
  is_active: true,
  description: '',
};

const SmsPackPlansPage = () => {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [editingPack, setEditingPack] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchPacks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await superAdminAPI.getSmsPackPlans();
      setPacks(res.data || []);
    } catch (e) {
      setFeedback({ type: 'error', text: 'Erreur lors du chargement des packs SMS.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPacks(); }, [fetchPacks]);

  const fmt = (n) => Number(n || 0).toLocaleString('fr-FR');

  const unitPrice = form.sms_count && form.price && Number(form.sms_count) > 0
    ? (Number(form.price) / Number(form.sms_count)).toFixed(2)
    : null;

  const openCreate = () => {
    setEditingPack(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (pack) => {
    setEditingPack(pack);
    setForm({
      name: pack.name || '',
      sms_count: String(pack.sms_count || ''),
      price: String(pack.price || ''),
      badge: pack.badge || '',
      is_popular: pack.is_popular || false,
      is_active: pack.is_active !== false,
      description: pack.description || '',
    });
    setShowModal(true);
  };

  const handleFormChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.sms_count || !form.price) {
      setFeedback({ type: 'error', text: 'Nom, nombre de SMS et prix sont obligatoires.' });
      return;
    }
    setSaving(true);
    setFeedback(null);
    try {
      const payload = {
        name: form.name.trim(),
        sms_count: Number(form.sms_count),
        price: Number(form.price),
        badge: form.badge.trim() || null,
        is_popular: form.is_popular,
        is_active: form.is_active,
        description: form.description.trim() || null,
      };
      if (editingPack) {
        const res = await superAdminAPI.updateSmsPackPlan(editingPack.id, payload);
        setFeedback({ type: 'success', text: res.data?.message || 'Pack mis a jour.' });
      } else {
        const res = await superAdminAPI.createSmsPackPlan(payload);
        setFeedback({ type: 'success', text: res.data?.message || 'Pack cree avec succes.' });
      }
      setShowModal(false);
      setEditingPack(null);
      fetchPacks();
    } catch (err) {
      setFeedback({ type: 'error', text: err.response?.data?.error || 'Erreur lors de l enregistrement.' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (pack) => {
    try {
      const res = await superAdminAPI.updateSmsPackPlan(pack.id, { is_active: !pack.is_active });
      setFeedback({ type: 'success', text: res.data?.message || 'Statut mis a jour.' });
      fetchPacks();
    } catch {
      setFeedback({ type: 'error', text: 'Erreur lors de la mise a jour du statut.' });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await superAdminAPI.deleteSmsPackPlan(deleteTarget.id);
      setFeedback({ type: 'success', text: res.data?.message || 'Pack supprime.' });
      setDeleteTarget(null);
      fetchPacks();
    } catch (err) {
      setFeedback({ type: 'error', text: err.response?.data?.error || 'Erreur lors de la suppression.' });
    } finally {
      setDeleting(false);
    }
  };

  const activePacks   = packs.filter(p => p.is_active);
  const inactivePacks = packs.filter(p => !p.is_active);

  return (
    <div style={{ paddingBottom: 40 }}>
      <div className="page-header">
        <div>
          <h1 style={{ margin: 0 }}>Grilles Tarifaires SMS</h1>
          <p style={{ margin: '4px 0 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
            Definissez les packs de credits SMS que les agences pourront commander. Vous etes le seul a fixer les tarifs.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary btn-sm" onClick={fetchPacks}>
            <FiRefreshCw style={{ marginRight: 4 }} /> Actualiser
          </button>
          <button className="btn btn-sm" style={{ background: '#7c3aed', color: '#fff', fontWeight: 700 }} onClick={openCreate}>
            <FiPlus style={{ marginRight: 6 }} /> Nouveau Pack SMS
          </button>
        </div>
      </div>

      {feedback && (
        <div style={{
          marginBottom: 20, padding: '12px 16px', borderRadius: 8,
          borderLeft: '5px solid ' + (feedback.type === 'success' ? '#22c55e' : '#ef4444'),
          background: feedback.type === 'success' ? '#dcfce7' : '#fee2e2',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span style={{ fontWeight: 600, color: feedback.type === 'success' ? '#15803d' : '#b91c1c' }}>{feedback.text}</span>
          <button className="btn btn-sm btn-secondary" onClick={() => setFeedback(null)}>X</button>
        </div>
      )}

      <div className="grid-4" style={{ marginBottom: 28, gap: 16 }}>
        {[
          { label: 'Packs actifs', value: activePacks.length, color: '#22c55e', bg: '#dcfce7' },
          { label: 'Packs desactives', value: inactivePacks.length, color: '#94a3b8', bg: '#f1f5f9' },
          { label: 'Commandes approuvees', value: packs.reduce((a, p) => a + Number(p.approved_orders || 0), 0), color: '#7c3aed', bg: '#f5f3ff' },
          { label: 'Revenus SMS (XOF)', value: fmt(packs.reduce((a, p) => a + Number(p.revenue_xof || 0), 0)), color: '#ea580c', bg: '#fff7ed' },
        ].map((kpi, i) => (
          <div key={i} className="card" style={{ background: kpi.bg, padding: '16px 20px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: kpi.color }}>{kpi.label}</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: kpi.color, marginTop: 4 }}>{kpi.value}</div>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="loading"><div className="spinner" /></div>
      ) : (
        <>
          <div className="card" style={{ marginBottom: 24 }}>
            <div className="card-header">
              <h2>Packs Actifs ({activePacks.length}) - Visibles et commandables par les agences</h2>
            </div>
            <div style={{ padding: 20 }}>
              {activePacks.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8' }}>
                  <FiPackage size={40} style={{ marginBottom: 12, opacity: 0.4 }} />
                  <p>Aucun pack actif. Creez votre premier pack SMS.</p>
                </div>
              ) : (
                <div className="grid-4" style={{ gap: 20 }}>
                  {activePacks.map(pack => (
                    <PackCard key={pack.id} pack={pack} onEdit={() => openEdit(pack)} onToggle={() => handleToggleActive(pack)} onDelete={() => setDeleteTarget(pack)} fmt={fmt} />
                  ))}
                </div>
              )}
            </div>
          </div>

          {inactivePacks.length > 0 && (
            <div className="card">
              <div className="card-header">
                <h2>Packs Desactives ({inactivePacks.length}) - Non visibles pour les agences</h2>
              </div>
              <div style={{ padding: 20 }}>
                <div className="grid-4" style={{ gap: 20 }}>
                  {inactivePacks.map(pack => (
                    <PackCard key={pack.id} pack={pack} onEdit={() => openEdit(pack)} onToggle={() => handleToggleActive(pack)} onDelete={() => setDeleteTarget(pack)} fmt={fmt} inactive />
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {showModal && (
        <Modal isOpen onClose={() => { setShowModal(false); setEditingPack(null); }} title={editingPack ? 'Modifier : ' + editingPack.name : 'Nouveau Pack SMS'}>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label style={{ fontWeight: 600, fontSize: 13, marginBottom: 4, display: 'block' }}>Nom du Pack *</label>
                <input type="text" className="form-control" placeholder="Ex: Pack Pro" required value={form.name} onChange={e => handleFormChange('name', e.target.value)} />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label style={{ fontWeight: 600, fontSize: 13, marginBottom: 4, display: 'block' }}>Badge (optionnel)</label>
                <input type="text" className="form-control" placeholder="Ex: Populaire" value={form.badge} onChange={e => handleFormChange('badge', e.target.value)} />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label style={{ fontWeight: 600, fontSize: 13, marginBottom: 4, display: 'block' }}>Nombre de SMS *</label>
                <input type="number" className="form-control" placeholder="Ex: 250" min={1} required value={form.sms_count} onChange={e => handleFormChange('sms_count', e.target.value)} />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label style={{ fontWeight: 600, fontSize: 13, marginBottom: 4, display: 'block' }}>Prix Total (XOF) *</label>
                <input type="number" className="form-control" placeholder="Ex: 4500" min={1} required value={form.price} onChange={e => handleFormChange('price', e.target.value)} />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label style={{ fontWeight: 600, fontSize: 13, marginBottom: 4, display: 'block' }}>Prix Unitaire (auto)</label>
                <input type="text" className="form-control" readOnly value={unitPrice ? unitPrice + ' XOF/SMS' : '---'} style={{ background: '#f8fafc', color: '#7c3aed', fontWeight: 700 }} />
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: 14 }}>
              <label style={{ fontWeight: 600, fontSize: 13, marginBottom: 4, display: 'block' }}>Description (optionnel)</label>
              <textarea className="form-control" rows={2} value={form.description} onChange={e => handleFormChange('description', e.target.value)} />
            </div>

            <div style={{ display: 'flex', gap: 24, marginBottom: 20 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" style={{ width: 16, height: 16, accentColor: '#f59e0b' }} checked={form.is_popular} onChange={e => handleFormChange('is_popular', e.target.checked)} />
                <span style={{ fontWeight: 600, fontSize: 13 }}><FiStar style={{ color: '#f59e0b' }} /> Pack Populaire</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" style={{ width: 16, height: 16, accentColor: '#22c55e' }} checked={form.is_active} onChange={e => handleFormChange('is_active', e.target.checked)} />
                <span style={{ fontWeight: 600, fontSize: 13 }}>Pack Actif (visible par les agences)</span>
              </label>
            </div>

            {form.name && form.sms_count && form.price && (
              <div style={{ background: '#faf5ff', border: '1px solid #ddd6fe', padding: 12, borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
                Apercu : <strong>{form.name}</strong> - {fmt(Number(form.sms_count))} SMS pour <strong style={{ color: '#7c3aed' }}>{fmt(Number(form.price))} XOF</strong>
                {unitPrice && ' (soit ' + unitPrice + ' XOF/SMS)'}
                {form.badge && <span style={{ marginLeft: 8, background: '#7c3aed', color: '#fff', padding: '1px 8px', borderRadius: 10, fontSize: 11 }}>{form.badge}</span>}
                {!form.is_active && <span style={{ color: '#94a3b8', marginLeft: 8 }}>(Inactif)</span>}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={saving}>Annuler</button>
              <button type="submit" className="btn" disabled={saving} style={{ background: '#7c3aed', color: '#fff', fontWeight: 700 }}>
                {saving ? 'Enregistrement...' : editingPack ? 'Mettre a jour' : 'Creer le Pack SMS'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {deleteTarget && (
        <Modal isOpen onClose={() => setDeleteTarget(null)} title="Confirmer la suppression">
          <div style={{ marginBottom: 20 }}>
            <p>Voulez-vous supprimer le pack <strong>{deleteTarget.name}</strong> ?</p>
            {Number(deleteTarget.total_orders) > 0 ? (
              <div style={{ background: '#fffbeb', padding: 12, borderRadius: 8, fontSize: 13, color: '#92400e' }}>
                Ce pack a {deleteTarget.total_orders} commande(s). Il sera seulement <strong>desactive</strong> pour conserver l'historique.
              </div>
            ) : (
              <div style={{ background: '#fee2e2', padding: 12, borderRadius: 8, fontSize: 13, color: '#b91c1c' }}>
                Action irreversible. Le pack sera definitivement supprime.
              </div>
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <button className="btn btn-secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>Annuler</button>
            <button className="btn" onClick={handleDelete} disabled={deleting} style={{ background: '#ef4444', color: '#fff', fontWeight: 700 }}>
              {deleting ? 'Traitement...' : Number(deleteTarget.total_orders) > 0 ? 'Desactiver' : 'Supprimer'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

const PackCard = ({ pack, onEdit, onToggle, onDelete, fmt, inactive }) => (
  <div className="card" style={{ position: 'relative', border: pack.is_popular ? '2px solid #7c3aed' : '1px solid #e2e8f0', opacity: inactive ? 0.65 : 1, display: 'flex', flexDirection: 'column', padding: 0 }}>
    {pack.badge && (
      <div style={{ position: 'absolute', top: -12, right: 14, background: pack.is_popular ? '#7c3aed' : '#2563eb', color: '#fff', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 800, zIndex: 2 }}>
        {pack.badge}
      </div>
    )}
    <div style={{ padding: 18, flex: 1 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <h3 style={{ margin: 0, fontSize: 16 }}>
          {pack.is_popular && <FiStar style={{ color: '#f59e0b', marginRight: 4 }} />}
          {pack.name}
        </h3>
        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: pack.is_active ? '#dcfce7' : '#f1f5f9', color: pack.is_active ? '#15803d' : '#64748b' }}>
          {pack.is_active ? 'Actif' : 'Inactif'}
        </span>
      </div>
      {pack.description && <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 10px 0' }}>{pack.description}</p>}
      <div style={{ textAlign: 'center', padding: '12px 0', borderTop: '1px solid #f1f5f9', borderBottom: '1px solid #f1f5f9' }}>
        <div style={{ fontSize: 26, fontWeight: 800, color: inactive ? '#94a3b8' : '#7c3aed' }}>{fmt(pack.sms_count)} SMS</div>
        <div style={{ fontSize: 18, fontWeight: 700 }}>{fmt(pack.price)} XOF</div>
        <div style={{ fontSize: 11, color: '#64748b' }}>{pack.unit_price} XOF / SMS</div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: 10, fontSize: 11 }}>
        <div style={{ background: '#f8fafc', padding: '6px 8px', borderRadius: 6 }}>{pack.approved_orders || 0} approuvees</div>
        <div style={{ background: '#f8fafc', padding: '6px 8px', borderRadius: 6 }}>{pack.pending_orders || 0} en attente</div>
        <div style={{ background: '#f8fafc', padding: '6px 8px', borderRadius: 6, gridColumn: 'span 2' }}>Revenus: {fmt(pack.revenue_xof || 0)} XOF</div>
      </div>
    </div>
    <div style={{ display: 'flex', borderTop: '1px solid #f1f5f9' }}>
      <button title="Modifier" onClick={onEdit} style={{ flex: 1, padding: '10px 0', background: 'none', border: 'none', cursor: 'pointer', color: '#2563eb', fontWeight: 600, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
        <FiEdit2 size={13} /> Modifier
      </button>
      <div style={{ width: 1, background: '#f1f5f9' }} />
      <button title={pack.is_active ? 'Desactiver' : 'Activer'} onClick={onToggle} style={{ flex: 1, padding: '10px 0', background: 'none', border: 'none', cursor: 'pointer', color: pack.is_active ? '#64748b' : '#22c55e', fontWeight: 600, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
        {pack.is_active ? <FiToggleRight size={13} /> : <FiToggleLeft size={13} />}
        {pack.is_active ? 'Desactiver' : 'Activer'}
      </button>
      <div style={{ width: 1, background: '#f1f5f9' }} />
      <button title="Supprimer" onClick={onDelete} style={{ flex: 1, padding: '10px 0', background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontWeight: 600, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
        <FiTrash2 size={13} /> Supprimer
      </button>
    </div>
  </div>
);

export default SmsPackPlansPage;
